// 크라이시스 로컬 1차 필터 (§11 확정).
//   - 자기 지향 표현만 잡는다. 타인 지향 분노("죽이고 싶다", "패고 싶다")는 NOT crisis.
//   - 자해 *방법* 단어는 넣지 않는다 — 의도·고통 표현 중심.
//   - 이건 1차 트리거일 뿐. AI 2차가 맥락(과장 vs 진짜)을 확정한다.
//   - 출시 전 안전·임상 검수로 보강하고 시장별 현지화 필수.

import type { Lang } from "./types";

const KR_PATTERNS: RegExp[] = [
  /죽고\s*싶/,
  /사라지고\s*싶/,
  /없어지고\s*싶/,
  /살기\s*싫/,
  /살고\s*싶지\s*않/,
  /더는?\s*못\s*살/,
  /자해/,
  /자살/,
  /내가\s*없어지면/,
];

const EN_PATTERNS: RegExp[] = [
  /\bwant to die\b/i,
  /\bwanna die\b/i,
  /\bkill myself\b/i,
  /\bkms\b/i,
  /\bend my life\b/i,
  /\bdon'?t want to live\b/i,
  /\bcan'?t go on\b/i,
  /\bsuicidal\b/i,
  /\bhurt myself\b/i,
  /\bno reason to live\b/i,
];

// 일본어 자기 지향 표현만(타인 지향 "殺したい"는 분노로 제외). 자해 방법 단어는 안 넣음.
const JA_PATTERNS: RegExp[] = [
  /死にたい/,
  /消えたい/,
  /いなくなりたい/,
  /生きていたくない/,
  /生きるのがつら/,
  /もう生きられない/,
  /自殺/,
  /自傷/,
  /リスカ/,
];

/** 로컬 1차 트리거 여부. true면 AI 2차로 맥락 확정해야 함. */
export function localCrisisTrigger(text: string): boolean {
  const t = text.normalize("NFC");
  return (
    KR_PATTERNS.some((re) => re.test(t)) ||
    EN_PATTERNS.some((re) => re.test(t)) ||
    JA_PATTERNS.some((re) => re.test(t))
  );
}

// 언어별 핫라인 (디자인 핸드오프). ⚠️ 번호·운영시간 변동 — 출시 전 출처 대조 재검증 필수
// (KR 보건복지부 / US 988lifeline.org / JP 厚労省).
export interface HotlineLink {
  label: string;
  url: string;
}
export interface Hotline {
  primary: string; // 1순위(굵게)
  more: (string | HotlineLink)[]; // 접는 "다른 창구". 링크 객체면 탭 가능.
}

export const HOTLINES: Record<Lang, Hotline> = {
  ko: {
    primary: "자살예방 상담전화 109",
    // 마들랜 = 보건복지부·한국생명존중희망재단 운영 공식 자살예방 SNS 상담(카카오톡 채널).
    more: [
      "정신건강 상담전화 1577-0199",
      "청소년 상담전화 1388",
      { label: "카카오톡 채널 ‘마들랜’", url: "https://pf.kakao.com/_DAxbYG" },
    ],
  },
  ja: {
    primary: "よりそいホットライン 0120-279-338",
    more: ["こころの健康相談統一ダイヤル 0570-064-556", "いのちの電話 0570-783-556 / 0120-783-556"],
  },
  en: {
    primary: "988 Suicide & Crisis Lifeline",
    more: ["Crisis Text Line: text HOME to 741741", "The Trevor Project 1-866-488-7386", "988lifeline.org"],
  },
};

export function hotlineFor(lang: Lang): Hotline {
  return HOTLINES[lang] ?? HOTLINES.en;
}
